import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'antd';
import { ListProps, useDataProvider, useNotify } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';

import { Link } from 'react-router-dom';
import { RoutableTabConfig } from '@src/types';
import MainCard from '@src/components/Common/MainCard';
import RoutableTabs from '@src/components/Common/RoutableTabs';
import { getActiveMembership } from '@src/components/Common/utils';
import { ADMIN_POV, usePov } from '@src/components/Common/pov';
import useGetGeneralSettings from '@src/providers/useGetGeneralSettings';
import ActivityTab from './ActivityTab';
import ApiKeysTab from './ApiKeysTab';
import MappingsTab from './MappingsTab';
import MembersTab from './MembersTab';
import MessagesTab from './MessagesTab';
import MetadataTab from './MetadataTab';
import SettingsTab from './SettingsTab';
import PaginatedSelect, { PAGE_SIZE } from '@src/components/Common/PaginatedSelect';
import './TeamSettings.css';

/**
 * What someone with no team lands on. A join request that is waiting is worth
 * saying so, because the answer to "why can I not see anything" is that
 * somebody else has not answered yet.
 */
const NoTeam = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [pending, setPending] = useState(false);

    useEffect(() => {
        dataProvider
            .getList('team_join_requests', {
                pagination: { page: 1, perPage: 20 },
                sort: { field: 'name', order: 'asc' },
                filter: {},
            })
            .then((response: any) => {
                if (response.data[0]) setPending(!response.data[0].is_accepted);
            })
            .catch(() => notify('Error checking for pending join requests', 'error'));
    }, []);

    return (
        <div className="netorca-team netorca-frame">
            <MainCard className="tm-panel">
                <div className="tm-nothing">
                    <div className="tm-nothing__title">
                        {pending ? 'Your join request is waiting' : 'You are not part of a team yet'}
                    </div>
                    <p className="tm-nothing__body">
                        {pending
                            ? 'An admin of the team you asked to join has to accept the request before you can act as that team.'
                            : 'Everything in NetOrca belongs to a team — its services, its declarations and its keys. Create one to get started.'}
                    </p>
                    {!pending && (
                        <Link to="/teams/create">
                            <Button type="primary">Create team</Button>
                        </Link>
                    )}
                </div>
            </MainCard>
        </div>
    );
};

/**
 * The team you are acting as, and everything about it: who is on it, what it
 * is allowed to do, the keys that speak for it, and what it has been doing.
 *
 * Which tabs there are depends on the role: administering a team is the
 * admin's, so a general member is not shown those tabs at all rather than
 * shown them disabled. Team mappings need LDAP to be on to mean anything.
 */
const TeamSettingsPage = (_props: ListProps) => {
    const generalSettings = useGetGeneralSettings();
    const ldap = generalSettings.find((setting) => setting.name === 'LDAP');
    const admin = usePov() === ADMIN_POV;

    const activeMembership = getActiveMembership();
    const ownTeam = activeMembership?.team;

    /* Which team the page is about. Only a platform admin can change it, and
       the page opens on the team they are acting as, so the two points of view
       start from the same place. Held here rather than in the Members tab
       because every tab is about this team, not just that one. */
    const [picked, setPicked] = useState<{ id: number; name: string } | undefined>(ownTeam);
    const dataProvider: ExtendedDataProvider = useDataProvider();

    /* One page of the picker. Production holds more teams than a dropdown
       should load at once, so the list action is paged and searched by name. */
    const fetchTeamsPage = useCallback(
        (search: string, page: number) =>
            dataProvider
                .getList('all_teams', {
                    pagination: { page, perPage: PAGE_SIZE },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { name: search },
                })
                .then(({ data, total }: any) => ({
                    options: data.map((t: any) => ({ value: t.id, label: t.name })),
                    more: page * PAGE_SIZE < total,
                })),
        [dataProvider],
    );

    if (!activeMembership?.active) return <NoTeam />;

    const team = { ...ownTeam, id: picked?.id ?? ownTeam.id };
    const teamName = picked?.name ?? ownTeam.name;
    const isAdmin = activeMembership.role === 'ADMIN';
    /* Messages are notices a team publishes to the consumers of its services,
       so only a service owner has any to write. Under the consumer POV the tab
       offered an editor for something the team cannot author from here. */
    const isServiceOwnerPov = localStorage.getItem('pov_preference') === 'SERVICE_OWNER';

    /* Keyed by the team, so a tab that has already fetched one team's data
       fetches again rather than showing it under another team's name. */
    const tabs: RoutableTabConfig[] = [
        { key: 'members', tab: 'Members', content: <MembersTab admin={admin} teamId={team.id} teamName={teamName} /> },
        { key: 'metadata', tab: 'Metadata', content: <MetadataTab key={team.id} teamId={team.id} allTeams={admin} /> },
    ];

    if (isAdmin) {
        tabs.push({ key: 'api-keys', tab: 'API Keys', content: <ApiKeysTab /> });
    }

    /* A platform admin manages any team's mappings whatever role they hold on
       their own team; a team admin manages their own team's. */
    if (ldap?.ENABLED && (admin || isAdmin)) {
        tabs.push({ key: 'team-mappings', tab: 'Team Mappings', content: <MappingsTab key={team.id} teamId={team.id} allTeams={admin} /> });
    }

    if (isAdmin) {
        tabs.push({ key: 'team-settings', tab: 'Team Settings', content: <SettingsTab key={team.id} teamId={team.id} allTeams={admin} /> });
        if (isServiceOwnerPov) {
            tabs.push({ key: 'messages', tab: 'Messages', content: <MessagesTab /> });
        }
    }

    tabs.push({ key: 'activity', tab: 'Activity', content: <ActivityTab key={team.id} teamId={team.id} allTeams={admin} /> });

    return (
        <div className="netorca-team netorca-frame">
            <MainCard className="tm-panel">
                <RoutableTabs
                    className="tm-tabs"
                    tabs={tabs}
                    defaultActiveKey="members"
                    queryParam="tab"
                    /* Which team the whole page is about, at the end of the
                       strip the tabs are on — one control above all of them
                       rather than a column repeated inside one of them. */
                    tabBarExtraContent={admin ? (
                        <div className="tm-team-pick">
                            <span className="tm-team-pick__label">Team</span>
                            <PaginatedSelect
                                className="tm-team-pick__select filter-select"
                                value={team.id}
                                data-cy="admin-team-picker"
                                aria-label="Team"
                                /* the team being acted as is listed whatever
                                   page it is on, so the control is never blank */
                                pinned={[{ value: team.id, label: teamName }]}
                                fetchPage={fetchTeamsPage}
                                onChange={(value: number, option: any) =>
                                    setPicked({ id: value, name: option?.label ?? teamName })
                                }
                            />
                        </div>
                    ) : undefined}
                />
            </MainCard>
        </div>
    );
};

export default TeamSettingsPage;
